import React, { FC, ReactNode } from "react"
import { FormattedMessage } from "react-intl"
import { getPiiProps } from "#/services/tracking"
import {
  Box,
  Stack,
  Typography,
  UnorderedList,
  UnorderedListItem,
  ListItemText,
  useTheme
} from "@carrotfertility/carotene-core"
import { useMemberName } from "#/services/user/derived-selectors/member-info"

interface Thread {
  date: string
  subject: string
}

type ThreadSubjectAndDateProps = {
  thread: Thread
}

type ThreadContainerProps = {
  children: ReactNode | ReactNode[]
  onClick: () => void
}

type ThreadSideTickProps = {
  active: boolean
}

type StyledThreadsListProps = {
  children: React.ReactNode | React.ReactNode[]
}

export function ThreadSideTick({ active }: ThreadSideTickProps): JSX.Element {
  const borderColor = (theme: any) => (active ? theme.palette.primary.accent : theme.palette.background.paper)

  return (
    <Box
      display="flex"
      height="80px"
      border={(theme) => `solid ${theme.tokens.borderWidth.md}`}
      borderColor={borderColor}
    />
  )
}
export function DefaultThreadMessage(): JSX.Element {
  const theme = useTheme()
  const memberName = useMemberName()

  if (memberName.isLoading) {
    return null
  }

  return (
    <Stack
      spacing={theme.tokens.spacing.md}
      sx={{
        marginInlineStart: (theme) => theme.tokens.spacing.xxl,
        marginInlineEnd: (theme) => theme.tokens.spacing.md
      }}
    >
      <Typography variant="h3" component="h2">
        <FormattedMessage defaultMessage="Get Started with Messages" />
      </Typography>
      <Typography variant="body1">
        <FormattedMessage defaultMessage="Hi {memberFirstName}," values={{ memberFirstName: memberName.data }} />
      </Typography>
      <Typography variant="body1">
        <FormattedMessage
          defaultMessage="Welcome! In this space, you’ll find your conversations with us. 
          Send us a message anytime and we’ll do our best to respond within one business day. 
          And don't worry — we'll continue to send our replies to your contact email address as well."
        />
      </Typography>
      {/* `component="div"`, and it is a correctness fix rather than a preference. `body1` renders a `<p>`,
          and this one contains a `<Box>` (div), an `<UnorderedList>` (ul) and another `Typography` — none
          of which may appear inside a paragraph. React says so ("validateDOMNesting: <p> cannot appear as
          a descendant of <p>"), and the browser does something worse than complain: it implicitly CLOSES
          the paragraph before the div, so the DOM it builds is not the tree React thinks it rendered, and
          the salutation below ends up a sibling of the list rather than following it.
          The variant is unchanged, so the type is identical — only the element differs. */}
      <Typography variant="body1" component="div">
        <FormattedMessage
          defaultMessage="Don't see something you're looking for? 
          There are a few reasons that might happen:"
        />
        <Box>
          <UnorderedList>
            <UnorderedListItem>
              <ListItemText
                primary={
                  <FormattedMessage
                    defaultMessage="Some notifications, such as Carrot Expert chat reminders, 
                    are sent only via email."
                  />
                }
              />
            </UnorderedListItem>
            <UnorderedListItem>
              <ListItemText
                primary={
                  <FormattedMessage
                    defaultMessage="At this time, only members can receive messages here. 
                    If an eligible partner sends us a message, our reply will appear in their email."
                  />
                }
              />
            </UnorderedListItem>
          </UnorderedList>
        </Box>
        <FormattedMessage defaultMessage="Best," description="message salutation" />
        {/* `component="div"` for the same reason as its parent: `ListItemText`'s own root is a `<div>`,
            so a `<p>` around it is invalid however the paragraph above it is spelled. */}
        <Typography variant="body1" component="div">
          <ListItemText primary={<FormattedMessage defaultMessage="Carrot Care Team" />} />
        </Typography>
      </Typography>
    </Stack>
  )
}

export const ThreadSubjectAndDate: FC<ThreadSubjectAndDateProps> = ({ thread }): JSX.Element => {
  return (
    <Stack
      spacing="none"
      sx={{
        overflow: "hidden",
        paddingBlockStart: (theme) => theme.spacing(theme.tokens.spacing.md),
        paddingBlockEnd: (theme) => theme.spacing(theme.tokens.spacing.md),
        paddingInlineStart: (theme) => theme.spacing(theme.tokens.spacing.md)
      }}
      width="100%"
    >
      <Typography variant="overline">{thread.date}</Typography>
      <Typography
        {...getPiiProps()}
        variant="body1"
        sx={{
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block"
        }}
      >
        {thread.subject}
      </Typography>
    </Stack>
  )
}

export function ThreadCardContainer({ children, onClick }: ThreadContainerProps): JSX.Element {
  return (
    <Box
      tabIndex={0}
      padding={0}
      component="button"
      display="flex"
      textAlign="start"
      borderTop={0}
      borderRight={0}
      borderBottom={(theme) => `solid ${theme.tokens.borderWidth.sm} ${theme.palette.background.hover}`}
      borderLeft={0}
      bgcolor={(theme) => theme.palette.background.paper}
      sx={{ overflow: "none" }}
      onClick={onClick}
      width="100%"
      paddingInlineEnd={(theme) => theme.spacing(theme.tokens.spacing.md)}
    >
      {children}
    </Box>
  )
}

export function StyledThreadsList({ children }: StyledThreadsListProps): JSX.Element {
  return (
    <Box
      data-testid="threads-list-scroll"
      sx={{
        // TAKES THE SLACK in the bounded column, rather than standing at a fixed height. This used to be
        // `min-block-size: 400px; block-size: 100%`, which suited the unbounded page it lived on: the
        // floor stopped a one-row column collapsing, and `100%` meant nothing without a bounded parent.
        //
        // Both are wrong now that the page is bounded to the viewport. A 400px floor inside a clipping
        // column cannot be scrolled to — the box is not overflowing ITSELF, so it grows no scrollbar of
        // its own and the rows past the clip are unreachable, which is a real defect on a short viewport
        // rather than a cosmetic one. `flex: 1` with `min-block-size: 0` is the pair that makes this box
        // exactly as tall as the space the column gives it, and scroll whatever does not fit.
        flex: 1,
        minBlockSize: 0,
        bgcolor: (theme) => theme.palette.background.paper,
        overflowX: "hidden",
        overflowY: "auto"
      }}
    >
      {children}
    </Box>
  )
}
